import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { MessagingTemplateDetailsComponent } from "./messaging-template-details-component/messaging-template-details-component";
import { CardContainer } from "@containers/common/card-container/card-container";
import { resources } from "@constants/hardcoded-strings";
import { getMessagingTemplateDetailsReduxRequest } from "@redux-requests/messaging-template-details";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { MESSAGING_TEMPLATE_DETAILS_REFERENCE_DATA_TYPES, MessagingTemplateDetailsReferenceData } from "@constants/reference-data";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { FormattedMessagingTemplate } from "@idw-types/data/messaging-template";
import { updateMessagingTemplateDetailsReduxRequest } from "@redux-requests/messaging-template-details/update-messaging-template-details";
import { removeUnusedVariablesFromMessagingTemplateLanguageFiles } from "@utils/messaging-template-util";
import { getSessionState } from "@redux/selectors/root-selectors";
import { getMessagingTemplateDetailsDataState } from "@redux/selectors/messaging-template-details-selectors";

export const MessagingTemplateDetailsContainer = () => {
    const dispatch = useDispatch();
    const { messagingTemplateId } = useParams<{ messagingTemplateId: string }>();

    const { session } = useSelector(getSessionState);
    const {
        isLoadingMessagingTemplate,
        messagingTemplate
    } = useSelector(getMessagingTemplateDetailsDataState);
    const referenceData = useReferenceDataSelector<MessagingTemplateDetailsReferenceData>(MESSAGING_TEMPLATE_DETAILS_REFERENCE_DATA_TYPES)

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadMessagingTemplate, setShouldLoadMessagingTemplate] = useState(true);


    const handleSave = async (messagingTemplateState: FormattedMessagingTemplate): Promise<FormattedMessagingTemplate> => {
        const newLanguageFiles = removeUnusedVariablesFromMessagingTemplateLanguageFiles(
            messagingTemplateState.type,
            messagingTemplateState.template,
            messagingTemplateState.languageFiles
        );

        const response = await updateMessagingTemplateDetailsReduxRequest(dispatch)({
            ...messagingTemplateState,
            languageFiles: newLanguageFiles
        }, referenceData.axnCompanies, referenceData.axnRelyingParties);
        return response;
    };

    useEffect(() => {
        if (!referenceData.isLoading.messagingTemplateDetails && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                MESSAGING_TEMPLATE_DETAILS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.AXNADMIN_CORE,
                CommonReferenceDataPageTypes.MESSAGING_TEMPLATE_DETAILS
            );
        }
    }, [referenceData.isLoading.messagingTemplateDetails, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCompanies &&
            referenceData.axnRelyingParties
        );
    }, [
        referenceData.axnCompanies,
        referenceData.axnRelyingParties
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingMessagingTemplate && shouldLoadMessagingTemplate) {
            setShouldLoadMessagingTemplate(false);
            getMessagingTemplateDetailsReduxRequest(dispatch)(messagingTemplateId, referenceData.axnCompanies, referenceData.axnRelyingParties);
        }
    }, [isReferenceDataPresent, isLoadingMessagingTemplate, shouldLoadMessagingTemplate]);

    const isLoading = useMemo(() => {
        return isLoadingMessagingTemplate || referenceData.isLoading.messagingTemplateDetails;
    }, [isLoadingMessagingTemplate, referenceData.isLoading.messagingTemplateDetails]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            messagingTemplate &&
            isReferenceDataPresent &&
            !shouldLoadMessagingTemplate &&
            !shouldLoadReferenceData
        );
    }, [isLoading, messagingTemplate, isReferenceDataPresent, shouldLoadMessagingTemplate, shouldLoadReferenceData]);

    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.messagingTemplateDetails.header} />
            <ContentPageBody isMaxWidth={true}>
                {isLoaded && (
                    <MessagingTemplateDetailsComponent
                        session={session}
                        initialMessagingTemplate={messagingTemplate}
                        axnCompanies={referenceData.axnCompanies}
                        axnRelyingParties={referenceData.axnRelyingParties}
                        handleSave={handleSave}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};